import type { ReactNode } from 'react';

import { DashboardsPage } from '../dashboards/DashboardsPage';
import { SearchInput } from '../dashboards/primitives/SearchInput';
import { FilterTabs } from '../shared/list-page/FilterTabs';
import { ListPageHeader } from '../shared/list-page/ListPageHeader';
import { ListPageLayout } from '../shared/list-page/ListPageLayout';
import { ListPageResults } from '../shared/list-page/ListPageResults';

import { GiftPassBundleCard } from './components/GiftPassBundleCard';
import { GiftPassBundlesTable } from './components/GiftPassBundlesTable';
import {
  BUNDLE_LIST_TABS,
  BUNDLE_TAB_LABELS,
  bundleCountLabel,
  bundleEmptyState,
  pricingRuleText,
} from './gift-pass-bundles-list-model';
import { useGiftPassBundlesListView } from './use-gift-pass-bundles-list-view';

import './components/gift-pass-bundles-list.css';

const RESULTS_ID = 'gift-pass-bundles-results';

export interface GiftPassBundlesListPageProps {
  actions?: ReactNode;
  canOpenBundles?: boolean;
}

export default function GiftPassBundlesListPage({
  actions,
  canOpenBundles = false,
}: GiftPassBundlesListPageProps) {
  const view = useGiftPassBundlesListView();

  return (
    <DashboardsPage
      header={
        <ListPageHeader
          title="Gift pass bundles"
          actions={actions}
          below={
            view.pricing && <p className="gpb-pricing-note">{pricingRuleText(view.pricing)}</p>
          }
        />
      }
    >
      <ListPageLayout
        resultsId={RESULTS_ID}
        countLabel={view.loaded ? bundleCountLabel(view.rows.length) : null}
        search={
          <SearchInput
            value={view.search}
            onChange={view.setSearch}
            placeholder="Search bundles…"
            ariaLabel="Search bundles"
            style={{ width: '100%' }}
          />
        }
        tabs={
          <FilterTabs
            tabs={BUNDLE_LIST_TABS}
            labels={BUNDLE_TAB_LABELS}
            counts={view.counts}
            active={view.tab}
            onChange={view.setTab}
            panelId={RESULTS_ID}
          />
        }
      >
        <ListPageResults
          noun="bundles"
          loading={view.loading}
          failure={view.failure}
          onRetry={view.retry}
          empty={view.rows.length === 0 ? bundleEmptyState(view.tab, view.search) : null}
          table={<GiftPassBundlesTable bundles={view.rows} canOpen={canOpenBundles} />}
          cards={view.rows.map((bundle) => (
            <GiftPassBundleCard key={bundle.id} bundle={bundle} canOpen={canOpenBundles} />
          ))}
        />
      </ListPageLayout>
    </DashboardsPage>
  );
}
